import { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  BarChart,
  Bar,
} from "recharts";
import {
  ComposableMap,
  Geographies,
  Geography,
} from "react-simple-maps";
import { adminApi } from "@/lib/adminAuth";
import { Globe2, Users, Eye, TrendingUp } from "lucide-react";

const GEO_URL =
  "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

const RANGES = [
  { key: "24h", label: "24h" },
  { key: "7d", label: "7 days" },
  { key: "30d", label: "30 days" },
  { key: "90d", label: "90 days" },
  { key: "all", label: "All" },
];

export default function Analytics() {
  const [range, setRange] = useState("7d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hoverCountry, setHoverCountry] = useState(null);

  const load = async (r) => {
    setLoading(true);
    try {
      const res = await adminApi.get(`/admin/analytics?range=${r}`);
      setData(res.data);
    } catch {
      setData({ error: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(range);
  }, [range]);

  const countryMap = useMemo(() => {
    const m = {};
    (data?.countries || []).forEach((c) => {
      if (c.country_code) m[c.country_code.toUpperCase()] = c.count;
    });
    return m;
  }, [data]);

  const maxCountry = useMemo(
    () => Math.max(1, ...Object.values(countryMap)),
    [countryMap]
  );

  const colorFor = (count) => {
    if (!count) return "#1a1a1a";
    const t = Math.log(count + 1) / Math.log(maxCountry + 1);
    // Interpolate dark → red
    const r = Math.round(20 + t * (225 - 20));
    const g = Math.round(20 + t * (6 - 20));
    const b = Math.round(20 + t * (0 - 20));
    return `rgb(${r}, ${g}, ${b})`;
  };

  return (
    <div data-testid="admin-analytics" className="space-y-8">
      {/* Range selector */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#1E6FE0] mb-1">
            Site Analytics
          </p>
          <h2 className="font-display text-4xl uppercase leading-none">
            Visitor <span className="text-[#E10600]">Overview</span>
          </h2>
        </div>
        <div className="flex gap-1 border border-[#262626]">
          {RANGES.map((r) => (
            <button
              key={r.key}
              data-testid={`analytics-range-${r.key}`}
              onClick={() => setRange(r.key)}
              className={`px-3 py-2 text-[10px] tracking-[0.2em] uppercase font-bold transition-colors ${
                range === r.key
                  ? "bg-[#E10600] text-white"
                  : "text-zinc-400 hover:text-white"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {loading && !data ? (
        <p className="text-zinc-500 font-mono">Loading…</p>
      ) : data?.error ? (
        <div className="border border-[#E10600] bg-[#E10600]/10 p-6">Failed to load analytics.</div>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#262626] border border-[#262626]">
            <Kpi icon={Eye} label="Total Visits" value={data?.total_visits ?? 0} color="#E10600" />
            <Kpi icon={Users} label="Unique Visitors" value={data?.unique_visitors ?? 0} color="#1E6FE0" />
            <Kpi
              icon={Globe2}
              label="Countries"
              value={data?.countries?.length ?? 0}
              color="#E10600"
            />
            <Kpi
              icon={TrendingUp}
              label="Top Page"
              value={data?.top_pages?.[0]?.path || "—"}
              color="#1E6FE0"
              small
            />
          </div>

          {/* Time series */}
          <Card title="Visits Over Time">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data?.series || []}>
                  <defs>
                    <linearGradient id="redG" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#E10600" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#E10600" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="blueG" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#1E6FE0" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#1E6FE0" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
                  <XAxis dataKey="date" stroke="#525252" tick={{ fontSize: 10 }} />
                  <YAxis stroke="#525252" tick={{ fontSize: 10 }} />
                  <Tooltip
                    contentStyle={{
                      background: "#0A0A0A",
                      border: "1px solid #E10600",
                      borderRadius: 0,
                      color: "#FFFFFF",
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="visits"
                    stroke="#E10600"
                    strokeWidth={2}
                    fill="url(#redG)"
                    name="Visits"
                  />
                  <Area
                    type="monotone"
                    dataKey="visitors"
                    stroke="#1E6FE0"
                    strokeWidth={2}
                    fill="url(#blueG)"
                    name="Unique"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>

          {/* World map + Top countries side-by-side */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <Card title="Where Visitors Are From">
                <div className="relative">
                  <ComposableMap
                    projection="geoEqualEarth"
                    projectionConfig={{ scale: 150 }}
                    style={{ width: "100%", height: "auto", background: "#050505" }}
                  >
                    <Geographies geography={GEO_URL}>
                      {({ geographies }) =>
                        geographies.map((geo) => {
                          const geoName = (geo.properties.name || "").toLowerCase();
                          // Account for common naming differences between the
                          // world atlas TopoJSON and ipapi country names.
                          const aliases = {
                            "united states of america": "united states",
                            "russian federation": "russia",
                            "republic of korea": "south korea",
                            "dem. rep. korea": "north korea",
                            "viet nam": "vietnam",
                            "iran (islamic rep. of)": "iran",
                            "syrian arab republic": "syria",
                            "lao pdr": "laos",
                            "czechia": "czech republic",
                          };
                          const normalized = aliases[geoName] || geoName;
                          const nameMatch = (data?.countries || []).find(
                            (c) =>
                              c.country &&
                              (c.country.toLowerCase() === geoName ||
                                c.country.toLowerCase() === normalized)
                          );
                          const count = nameMatch?.count || 0;
                          const fill = colorFor(count);
                          return (
                            <Geography
                              key={geo.rsmKey}
                              geography={geo}
                              fill={fill}
                              stroke="#262626"
                              strokeWidth={0.4}
                              style={{
                                default: { outline: "none" },
                                hover: { outline: "none", fill: "#E10600" },
                                pressed: { outline: "none" },
                              }}
                              onMouseEnter={() =>
                                setHoverCountry({
                                  name: geo.properties.name,
                                  count,
                                })
                              }
                              onMouseLeave={() => setHoverCountry(null)}
                            />
                          );
                        })
                      }
                    </Geographies>
                  </ComposableMap>
                  {hoverCountry && (
                    <div className="absolute top-3 right-3 bg-[#0A0A0A] border border-[#E10600] px-3 py-2">
                      <p className="text-[10px] tracking-[0.2em] uppercase font-mono text-[#E10600]">
                        {hoverCountry.name}
                      </p>
                      <p className="font-display text-2xl leading-none">
                        {hoverCountry.count.toLocaleString()}{" "}
                        <span className="text-xs text-zinc-500">visits</span>
                      </p>
                    </div>
                  )}
                </div>
              </Card>
            </div>
            <Card title="Top Countries">
              <ul className="space-y-2 max-h-80 overflow-y-auto pr-2">
                {(data?.countries || []).slice(0, 15).map((c, i) => (
                  <li
                    key={i}
                    className="flex items-center justify-between text-sm border-b border-[#262626] pb-2"
                  >
                    <span className="flex items-center gap-2">
                      <span className="text-[10px] font-mono text-zinc-500 w-5">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="text-zinc-200">{c.country || "Unknown"}</span>
                    </span>
                    <span
                      className="font-mono font-bold"
                      style={{ color: i === 0 ? "#E10600" : "#FFFFFF" }}
                    >
                      {c.count}
                    </span>
                  </li>
                ))}
                {(data?.countries || []).length === 0 && (
                  <li className="text-zinc-500 text-sm font-mono">No data yet.</li>
                )}
              </ul>
            </Card>
          </div>

          {/* Top pages + Top referrers */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card title="Top Pages">
              <List items={data?.top_pages || []} field="path" emptyLabel="—" />
            </Card>
            <Card title="Top Referrers">
              <List
                items={(data?.top_referrers || []).filter((r) => r.referrer)}
                field="referrer"
                emptyLabel="Direct / no referrer"
                truncate
              />
            </Card>
          </div>

          {/* Devices / OS / Browsers */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card title="Devices">
              <BarRow data={data?.devices || []} field="device" />
            </Card>
            <Card title="Operating Systems">
              <BarRow data={data?.operating_systems || []} field="os" />
            </Card>
            <Card title="Browsers">
              <BarRow data={data?.browsers || []} field="browser" />
            </Card>
          </div>
        </>
      )}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, color, small = false }) {
  return (
    <div className="bg-[#0A0A0A] p-5 md:p-6 hover:bg-[#121212] transition-colors">
      <Icon className="w-5 h-5 mb-3" style={{ color }} />
      <p
        className={`font-display uppercase leading-none ${
          small ? "text-lg truncate" : "text-4xl"
        }`}
      >
        {typeof value === "number" ? value.toLocaleString() : value}
      </p>
      <p
        className="mt-2 text-[10px] tracking-[0.2em] uppercase font-mono"
        style={{ color }}
      >
        {label}
      </p>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="border border-[#262626] bg-[#0A0A0A]">
      <div className="px-5 py-3 border-b border-[#262626] flex items-center justify-between">
        <p className="text-[10px] tracking-[0.25em] uppercase font-bold font-mono text-zinc-400">
          {title}
        </p>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function List({ items, field, emptyLabel, truncate = false }) {
  if (!items.length)
    return <p className="text-zinc-500 text-sm font-mono">{emptyLabel}</p>;
  const max = Math.max(...items.map((i) => i.count));
  return (
    <ul className="space-y-2">
      {items.slice(0, 10).map((it, i) => (
        <li key={i} className="text-sm">
          <div className="flex items-center justify-between">
            <span
              className={`text-zinc-200 ${truncate ? "truncate max-w-[80%]" : ""}`}
              title={it[field]}
            >
              {it[field] || "—"}
            </span>
            <span className="font-mono text-zinc-400 ml-2">{it.count}</span>
          </div>
          <div className="mt-1 h-1 bg-[#1a1a1a]">
            <div
              className="h-full bg-[#E10600]"
              style={{ width: `${(it.count / max) * 100}%` }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}

function BarRow({ data, field }) {
  if (!data.length)
    return <p className="text-zinc-500 text-sm font-mono">No data.</p>;
  return (
    <div className="h-44">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
          <XAxis dataKey={field} stroke="#525252" tick={{ fontSize: 10 }} />
          <YAxis stroke="#525252" tick={{ fontSize: 10 }} />
          <Tooltip
            contentStyle={{
              background: "#0A0A0A",
              border: "1px solid #1E6FE0",
              borderRadius: 0,
              color: "#FFFFFF",
            }}
          />
          <Bar dataKey="count" fill="#1E6FE0" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
